import _ from 'lodash'
import moment from 'moment'

import { Access } from 'modules/core/hooks/useCurrentSession'
import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

const AM = 'AM'
const PM = 'PM'

const leadingZero = (num: string | number): string => _.padStart(`${num}`, 2, '0')

export const RegexFor12H = /([0-9-]{1,2}):([0-9-]{1,2})\s{2}([AaPp][Mm])/

export const RegexFor24H = /([0-9-]{1,2}):([0-9-]{1,2})/

// NOTE: TIME_REGEX validates both formats, am/pm and 24h
export const TIME_REGEX = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/

export const EMPTY_TIME_REGEX = /--:--/

interface InputValuesWithoutMeridiem {
  hrs: string | number
  min: string | number
}

interface InputValuesWithMeridiem extends InputValuesWithoutMeridiem {
  meridiem: string
}

export const getStringValueWithMeridiem = (inputValues: InputValuesWithMeridiem) =>
  `${leadingZero(inputValues.hrs)}:${leadingZero(inputValues.min)}  ${inputValues.meridiem}`

export const getStringValueWithoutMeridiem = (inputValues: InputValuesWithoutMeridiem) =>
  `${leadingZero(inputValues.hrs)}:${leadingZero(inputValues.min)}`

// Receive 2:34[TWO SPACES HERE]PM format and returns 14:34
export const get24HFormat = (value: string) => {
  const result = RegexFor12H.exec(value)

  if (!result) return ''

  let hour = result[1]

  const minute = result[2]

  if (result[3] === AM && hour !== undefined && minute !== undefined) {
    hour = parseInt(hour) === 12 ? '0' : parseInt(hour).toString()

    return `${leadingZero(hour)}:${leadingZero(minute)}`
  } else if (hour !== undefined) {
    hour = parseInt(hour) === 12 ? '12' : (parseInt(hour) + 12).toString()

    return `${hour}:${minute}`
  }
}

// Receive 14:34 format and returns 02:34[TWO SPACES HERE]PM
export const get12HFormat = (value: string) => {
  const result = RegexFor24H.exec(value)

  if (!result || result[1] === undefined) return undefined

  let hour = +result[1]

  const minute = result[2] ?? '00'

  if (hour < 12) {
    hour = hour === 0 ? 12 : hour

    return getStringValueWithMeridiem({
      hrs: hour,
      min: minute,
      meridiem: AM,
    })
  } else {
    hour = hour === 12 ? 12 : hour - 12

    return getStringValueWithMeridiem({
      hrs: hour,
      min: minute,
      meridiem: PM,
    })
  }
}

// If receives 02:34[TWO SPACES HERE]PM format, returns 02:34pm OR keeps 14:34 if 24hrs
export const formatToLowerCase = (value: string): string => {
  return value.replace(/\s/g, '').toLowerCase()
}

// Receive time string ("07:30") and convert to "7h 30m"
export const convertToSeconds = (time: { value: string }) => {
  const [hours, minutes] = time.value.split(':')

  if (hours !== undefined && minutes !== undefined) {
    const hoursToSec = +hours * 3600
    const minutesToSec = +minutes * 60

    return hoursToSec + minutesToSec
  }

  return 0
}

export const formatSecondsToHM = (seconds: number): string => {
  const hours = Math.floor(+seconds / 3600)
  const minutes = Math.ceil(Math.floor(+seconds - hours * 3600) / 60)

  return `${Number(hours)}h ${Number(minutes)}m`
}

// Receive 28800 seconds and convert to "08:00"
export const formatSecondsToDayInputForm = (seconds: number): string => {
  const hours = Math.floor(+seconds / 3600)
  const formattedHours = hours > 9 ? Number(hours) : `0${Number(hours)}`
  const minutes = Math.ceil(Math.floor(+seconds - hours * 3600) / 60)
  const formattedMinutes = minutes > 9 ? Number(minutes) : `0${Number(minutes)}`

  return `${formattedHours}:${formattedMinutes}`
}

export const convertToSecondsOrNull = (minutes: number | null) => {
  return minutes === null ? null : minutes * 60
}

// Format minutes to compact text by removing spaces and "m" from minutesToText result
const formatMinutesToCompactText = (
  value: number | string,
  alwaysRemainder = false,
  withPlusSign = false,
  onlyMinutes = false,
  useColumnSeparator = false
): string => {
  const result = minutesToText(
    value,
    alwaysRemainder,
    withPlusSign,
    onlyMinutes,
    useColumnSeparator
  )

  // If it's the column separator format (e.g., "08:00"), return as is
  if (useColumnSeparator) {
    return result
  }

  // Transform "35h 15m" to "35h15" by removing spaces and "m"
  return result.replace(/\s+/g, '').replace(/m$/, '')
}

// convert decimals hours to time format. Eg: 1,5 => 1h 30mins / 1hour 30minutes
export const convertHoursToTimeFormat = (
  hoursInDecimal: number,
  extendedFormat = false,
  showShortFormat = false
): string => {
  const timeInSec = hoursInDecimal * 60 * 60
  const duration = moment.duration(timeInSec, 'seconds')
  const hours = Math.trunc(duration.asHours())
  const minutes = Math.round(duration.minutes() + duration.seconds() / 60)

  const hourAmount = hours
    ? extendedFormat
      ? i18n.tp('common.decimal_to_time.hours.extended.hour_amount', {
          count: hours,
        })
      : i18n.t('common.decimal_to_time.hours.basic.hour_amount', {
          total: hours,
        })
    : ''

  const minuteAmount = minutes
    ? extendedFormat
      ? i18n.tp('common.decimal_to_time.hours.extended.minute_amount', {
          count: minutes,
        })
      : i18n.t('common.decimal_to_time.hours.basic.minute_amount', {
          total: minutes,
        })
    : ''

  return showShortFormat
    ? formatMinutesToCompactText(hours * 60 + minutes)
    : [hourAmount, minuteAmount].filter(Boolean).join(' ')
}

export const formatTime = (access: Access, time: string) => {
  if (access.timeFormat === 24) return time

  return get12HFormat(time)
}
